import { useEffect, useRef, useState } from 'react'
import useDebounce from './useDebounce'
import useStateWithMerge from './useStateWithMerge'

const isEmptyObj = (obj) => Object.keys(obj).length === 0

const getInitialState = (defaultPageSize) => {
  const pageSize = defaultPageSize == null ? 10 : defaultPageSize
  return {
    pagination: { page: 1, pageSize },
    order: [],
    actualOrder: {},
  }
}

function useTableManager({
  onSearch,
  filterText,
  initialColumns = [],
  searchOnChange = [],
  extraFilters = {},
  rebuildColumnsOnChange = [],
  withPolling = false,
  defaultPageSize,
}) {
  const [state, setState] = useStateWithMerge(getInitialState(defaultPageSize))
  const [columns, setColumns] = useState([])
  const debouncedSearchItem = useDebounce(filterText, 2000)
  const { pagination, order, actualOrder } = state

  // En pantallas con polling, los cambios de filtro no pasan por el
  // useEffect de más abajo (que solo corre si !withPolling), por lo que
  // la página no se reiniciaba al cambiar un filtro, quedando en una
  // página que ya no existe para el nuevo resultado filtrado.
  // Se corrige la página aquí, durante el render, para que el efecto de
  // polling (que sí depende de `pagination.page`) dispare con la página
  // ya corregida y no se hagan dos búsquedas (una con la página vieja).
  // Se compara vía JSON.stringify (no por referencia) porque algunas
  // pantallas arman `extraFilters` con arrays nuevos en cada render
  // (p.ej. rango de fechas) aunque su contenido no haya cambiado.
  // Solo aplica a pantallas con polling: las demás ya resetean la
  // página correctamente vía el useEffect de más abajo.
  const filterSignature = withPolling
    ? JSON.stringify([
        debouncedSearchItem,
        ...searchOnChange,
        ...Object.values(extraFilters),
      ])
    : null
  const prevFilterSignatureRef = useRef(filterSignature)

  if (withPolling && prevFilterSignatureRef.current !== filterSignature) {
    prevFilterSignatureRef.current = filterSignature
    if (pagination.page !== 1) {
      setState({ pagination: { ...pagination, page: 1 } })
    }
  }

  // Función para gatillar una búsqueda
  // puede usarse para un retry
  function retrySearch() {
    if (!withPolling) {
      setState(getInitialState(defaultPageSize))
    }
    onSearch({
      pagination,
      order,
      filter: debouncedSearchItem,
      extraFilters,
    })
  }

  // Los filtros de las tablas no los solemos usar (2do parametro)
  function onTableChange(pagination, filtersTable, sorter) {
    const directions = {
      ascend: `ASC`,
      descend: `DESC`,
    }
    let field = null
    if (!isEmptyObj(sorter)) {
      field = sorter.column ? sorter.column.sorterPath : sorter.field
      if (field && sorter.order != null) {
        setState({
          order: [[...field.split(`.`), directions[sorter.order]]],
          actualOrder: sorter,
        })
      } else {
        setState({
          order: [],
          actualOrder: {},
        })
      }
    }
    if (!isEmptyObj(pagination)) {
      setState({
        pagination: {
          page: pagination.current,
          pageSize: pagination.pageSize,
        },
      })
    }
    if (!withPolling) {
      onSearch({
        pagination: { page: pagination.current, pageSize: pagination.pageSize },
        order: field
          ? [[...field.split(`.`), directions[sorter.order]]]
          : undefined,
        filter: debouncedSearchItem,
        extraFilters,
      })
    }
  }

  useEffect(() => {
    //Se reinicia la paginación y orders
    if (!withPolling) {
      retrySearch()
    }
  }, [debouncedSearchItem, ...searchOnChange, ...Object.values(extraFilters)])

  //  Se mapean las columnas cada vez que sortea una columna
  // eslint-disable-next-line max-len
  //  Es OBLIGATORIO que se defina un key en la columna para que funcione apropiadamente
  useEffect(() => {
    const columns = initialColumns.map((column) => {
      const extraProps = {}
      if (column.sorter) {
        extraProps.sortOrder =
          actualOrder.columnKey === column.key && actualOrder.order
      }
      return {
        ...column,
        ...extraProps,
      }
    })
    setColumns(columns)
  }, [actualOrder, ...rebuildColumnsOnChange])

  return {
    onTableChange,
    pagination,
    columns,
    retrySearch,
    debouncedSearchItem,
    order,
  }
}

export default useTableManager
